/**
 * @fileoverview Directory Approval page for institution approvals
 * Allows admins to review and approve/reject institutions in multi-step workflow
 *
 * Approval flow:
 * 1. PENDING_APPROVAL_GESTAO_LENA - Gestão Lena team approval
 * 2. PENDING_APPROVAL_REGULATORIO - Regulatório team approval
 * 3. PENDING_APPROVAL_DIRETORIA - Diretoria approval (only for Buyer category)
 * 4. APPROVED - Fully approved
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    intl,
    DmsPage,
    DmsAltBox,
    DmsTable,
    DmsTabber,
    fetchData,
    HTTP_STATUS,
    DmsButton,
    DmsModal,
} from "dmsbasereact";
import {
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HistoryIcon from "@mui/icons-material/History";
import GroupsIcon from "@mui/icons-material/Groups";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CategoryIcon from "@mui/icons-material/Category";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Swal from "sweetalert2";
import { getInstitutionCategoryLabel, getInstitutionCategoryOptions, InstitutionCategoryEnum } from "../../constants/InstitutionType";
import {
    InstitutionStatusEnum,
    CATEGORIES_REQUIRING_DIRECTOR_APPROVAL,
} from "../../constants/InstitutionStatus";
import { DmsFileViewerModal } from "../../components/DmsFileViewer";
import { useFormatters } from "../../hooks/useFormater";
import { downloadFile } from "../../utils/fileDownload";
import { getTranslatedDocumentName } from "../../utils/documentTranslations";
import { formatDate } from "../../utils/formatters";


export const DirectoryApprovePage = () => {
    const [searchParams] = useSearchParams();
    const tableAllRef = useRef(null);
    const tableGestaoLenaRef = useRef(null);
    const tableRegulatorioRef = useRef(null);
    const tableDiretoriaRef = useRef(null);
    const [selectedInstitution, setSelectedInstitution] = useState(null);
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [directorApprovalStatus, setDirectorApprovalStatus] = useState(null);
    const [directorContext, setDirectorContext] = useState(null);
    const [institutionDocuments, setInstitutionDocuments] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    // Initialize tab from URL parameter if present, default to "all"
    const initialTab = searchParams.get("tab") || "all";
    const validTabs = ["all", "gestao_lena", "regulatorio", "diretoria"];
    const [currentTab, setCurrentTab] = useState(
        validTabs.includes(initialTab) ? initialTab : "all"
    );
    const formatters = useFormatters();

    /**
     * Format categories display: If BUYER (104) is present, show "Canal de Venda + others"
     * @param {Array} categories - Array of category objects [{value: 100}, ...]
     * @returns {string} Formatted string like "Canal de Venda + Distribuidor"
     */
    const formatCategoriesForDisplay = (categories) => {
        if (!categories || categories.length === 0) return "-";

        const categoryValues = categories.map(c => c.value);
        const hasBuyer = categoryValues.includes(InstitutionCategoryEnum.Buyer);

        // Get all non-buyer categories
        const otherCategories = categoryValues.filter(v => v !== InstitutionCategoryEnum.Buyer);

        if (hasBuyer && otherCategories.length > 0) {
            // Format as "Canal de Venda + Other Categories"
            const otherLabels = otherCategories
                .map(v => getInstitutionCategoryLabel(v))
                .filter(label => label !== "-")
                .join(" + ");

            return `${intl.get("SalesChannel").d("Sales Channel")} + ${otherLabels}`;
        } else if (hasBuyer) {
            // Only buyer (shouldn't happen normally, but handle it)
            return intl.get("SalesChannel").d("Sales Channel");
        } else {
            // No buyer, just show categories normally separated by " + "
            return otherCategories
                .map(v => getInstitutionCategoryLabel(v))
                .filter(label => label !== "-")
                .join(" + ");
        }
    };

    // Category request specific state
    const categoryRequestTableRef = useRef(null);
    const [selectedCategoryRequest, setSelectedCategoryRequest] = useState(null);
    const [categoryRequestDetailOpen, setCategoryRequestDetailOpen] = useState(false);
    const [categoryRequestRejectOpen, setCategoryRequestRejectOpen] = useState(false);
    const [categoryRequestRejectReason, setCategoryRequestRejectReason] = useState("");
    const [categoryRequestApproveOpen, setCategoryRequestApproveOpen] = useState(false);
    const [categoryRequestApproveJustification, setCategoryRequestApproveJustification] = useState("");

    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [institutionToApprove, setInstitutionToApprove] = useState(null);
    const [approveJustification, setApproveJustification] = useState("");

    // Document preview state
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);

    const [approvalPermissions, setApprovalPermissions] = useState({
        can_approve_gestao_lena: false,
        can_approve_regulatorio: false,
        can_approve_diretoria: false,
        director_groups: [],
    });

    const refreshTable = useCallback(() => {
        tableAllRef.current?.updateTable();
        tableGestaoLenaRef.current?.updateTable();
        tableRegulatorioRef.current?.updateTable();
        tableDiretoriaRef.current?.updateTable();
    }, []);

    const refreshCategoryRequestTable = useCallback(() => {
        if (categoryRequestTableRef.current) {
            categoryRequestTableRef.current.updateTable();
        }
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const response = await fetchData("/admin/my-approval-permissions", "GET", true);
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                    setApprovalPermissions(response.data.data);
                }
            } catch (err) {
                console.error("Failed to fetch approval permissions:", err);
            }
        };
        fetchPermissions();
    }, []);

    const canApproveInCurrentTab = useCallback((row) => {
        if (currentTab === "all") {
            if (row.required_role === "EUROFARMA_GESTAO_LENA") {
                return approvalPermissions.can_approve_gestao_lena;
            } else if (row.required_role === "EUROFARMA_REGULATORIO") {
                return approvalPermissions.can_approve_regulatorio;
            } else if (row.required_role === "EUROFARMA_DIRETORIA") {
                return approvalPermissions.can_approve_diretoria;
            }
            return false;
        }

        if (currentTab === "gestao_lena") {
            return approvalPermissions.can_approve_gestao_lena;
        } else if (currentTab === "regulatorio") {
            return approvalPermissions.can_approve_regulatorio;
        } else if (currentTab === "diretoria") {
            return approvalPermissions.can_approve_diretoria;
        }
        return false;
    }, [currentTab, approvalPermissions]);

    const fetchApprovalHistory = useCallback(async (institutionPk) => {
        try {
            setLoadingHistory(true);
            const response = await fetchData(`/admin/${institutionPk}/approval-history`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                setApprovalHistory(response.data?.data || []);
            } else {
                setApprovalHistory([]);
            }
        } catch (err) {
            setApprovalHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    const fetchDirectorApprovalStatus = useCallback(async (institutionPk) => {
        try {
            const response = await fetchData(`/admin/${institutionPk}/director-approval-status`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                setDirectorApprovalStatus(response.data?.data || null);
            } else {
                setDirectorApprovalStatus(null);
            }
        } catch (err) {
            setDirectorApprovalStatus(null);
        }
    }, []);

    const fetchDirectorContext = useCallback(async (institutionPk) => {
        try {
            const response = await fetchData(`/admin/${institutionPk}/my-director-context`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                setDirectorContext(response.data?.data || null);
            } else {
                setDirectorContext(null);
            }
        } catch (err) {
            setDirectorContext(null);
        }
    }, []);

    const fetchInstitutionDocuments = useCallback(async (institutionPk) => {
        try {
            setLoadingDocuments(true);
            const response = await fetchData(`/institution/${institutionPk}/files`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                setInstitutionDocuments(response.data?.data || []);
            } else {
                setInstitutionDocuments([]);
            }
        } catch (err) {
            setInstitutionDocuments([]);
        } finally {
            setLoadingDocuments(false);
        }
    }, []);


    const handleViewDetails = useCallback(async (institution) => {
        setSelectedInstitution(institution);
        setDetailModalOpen(true);
        // For category requests the real institution pk is in institution_pk, not pk
        const realInstitutionPk = institution.record_type === "category_request"
            ? institution.institution_pk
            : institution.pk;
        await Promise.all([
            fetchApprovalHistory(realInstitutionPk),
            fetchInstitutionDocuments(realInstitutionPk),
        ]);
        if (institution.status === InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA) {
            await Promise.all([
                fetchDirectorApprovalStatus(realInstitutionPk),
                fetchDirectorContext(realInstitutionPk),
            ]);
        } else {
            setDirectorApprovalStatus(null);
            setDirectorContext(null);
        }
    }, [fetchApprovalHistory, fetchDirectorApprovalStatus, fetchDirectorContext, fetchInstitutionDocuments]);

    const handleCloseDetailModal = useCallback(() => {
        setDetailModalOpen(false);
        setSelectedInstitution(null);
        setApprovalHistory([]);
        setDirectorApprovalStatus(null);
        setDirectorContext(null);
        setInstitutionDocuments([]);
    }, []);

    const handlePreviewDocument = useCallback((doc) => {
        setPreviewDocument(doc);
        setPreviewModalOpen(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewModalOpen(false);
        setPreviewDocument(null);
    }, []);

    const handleApproveInstitution = useCallback((institution) => {
        setInstitutionToApprove(institution);
        setApproveJustification("");
        setApproveModalOpen(true);
    }, []);

    const executeApproveInstitution = useCallback(async () => {
        if (!institutionToApprove) return;
        try {
            setActionLoading(true);
            // Use different endpoint for category requests
            const isCategoryRequest = institutionToApprove.record_type === "category_request";
            const url = isCategoryRequest
                ? `/admin/category-requests/${institutionToApprove.pk}/approve`
                : `/admin/${institutionToApprove.pk}/approve`;

            const payload = {
                justification: approveJustification,
            };

            const response = await fetchData(url, "POST", true, payload);
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: isCategoryRequest
                        ? intl.get("CategoryRequestApproved").d("Category Request Approved")
                        : intl.get("InstitutionApproved").d("Institution Approved"),
                    text: response.data?.detail || (isCategoryRequest
                        ? intl.get("CategoryRequestApprovedMessage").d("The category request has been approved.")
                        : intl.get("InstitutionApprovedMessage").d("The institution has been approved successfully.")),
                });
                setApproveModalOpen(false);
                setInstitutionToApprove(null);
                handleCloseDetailModal();
                refreshTable();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("FailedToApproveInstitution").d("Failed to approve"),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToApproveInstitution").d("Failed to approve"),
            });
        } finally {
            setActionLoading(false);
        }
    }, [institutionToApprove, approveJustification, refreshTable, handleCloseDetailModal]);

    const handleOpenRejectModal = useCallback((institution) => {
        setSelectedInstitution(institution);
        setRejectReason("");
        setRejectModalOpen(true);
    }, []);

    const handleRejectInstitution = useCallback(async () => {
        if (!rejectReason.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("ReasonRequired").d("Reason Required"),
                text: intl.get("PleaseProvideRejectionReason").d("Please provide a reason for rejection."),
            });
            return;
        }

        try {
            setActionLoading(true);
            // Use different endpoint for category requests
            const isCategoryRequest = selectedInstitution.record_type === "category_request";
            const url = isCategoryRequest
                ? `/admin/category-requests/${selectedInstitution.pk}/reject`
                : `/admin/${selectedInstitution.pk}/reject`;

            const response = await fetchData(url, "POST", true, {
                justification: rejectReason,
            });
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: isCategoryRequest
                        ? intl.get("CategoryRequestRejected").d("Category Request Rejected")
                        : intl.get("InstitutionRejected").d("Institution Rejected"),
                    text: response.data?.detail || (isCategoryRequest
                        ? intl.get("CategoryRequestRejectedMessage").d("The category request has been rejected.")
                        : intl.get("InstitutionRejectedMessage").d("The institution has been rejected.")),
                });
                setRejectModalOpen(false);
                handleCloseDetailModal();
                refreshTable();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("FailedToRejectInstitution").d("Failed to reject"),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToRejectInstitution").d("Failed to reject"),
            });
        } finally {
            setActionLoading(false);
        }
    }, [rejectReason, selectedInstitution, refreshTable, handleCloseDetailModal]);

    const handleCloseCategoryRequestDetail = useCallback(() => {
        setCategoryRequestDetailOpen(false);
        setSelectedCategoryRequest(null);
    }, []);

    const handleApproveCategoryRequest = useCallback((request) => {
        setSelectedCategoryRequest(request);
        setCategoryRequestApproveJustification("");
        setCategoryRequestApproveOpen(true);
    }, []);

    const executeApproveCategoryRequest = useCallback(async () => {
        if (!selectedCategoryRequest) return;
        try {
            setActionLoading(true);
            const response = await fetchData(
                `/admin/category-requests/${selectedCategoryRequest.pk}/approve`,
                "POST",
                true,
                { justification: categoryRequestApproveJustification }
            );
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("CategoryRequestApproved").d("Category Request Approved"),
                    text: response.data?.detail || intl.get("CategoryRequestApprovedMessage").d("The category request has been approved."),
                });
                setCategoryRequestApproveOpen(false);
                handleCloseCategoryRequestDetail();
                refreshCategoryRequestTable();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("FailedToApproveCategoryRequest").d("Failed to approve category request"),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToApproveCategoryRequest").d("Failed to approve category request"),
            });
        } finally {
            setActionLoading(false);
        }
    }, [selectedCategoryRequest, categoryRequestApproveJustification, refreshCategoryRequestTable, handleCloseCategoryRequestDetail]);

    const handleOpenCategoryRequestRejectModal = useCallback((request) => {
        setSelectedCategoryRequest(request);
        setCategoryRequestRejectReason("");
        setCategoryRequestRejectOpen(true);
    }, []);

    const handleRejectCategoryRequest = useCallback(async () => {
        if (!categoryRequestRejectReason.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("ReasonRequired").d("Reason Required"),
                text: intl.get("PleaseProvideRejectionReason").d("Please provide a reason for rejection."),
            });
            return;
        }

        try {
            setActionLoading(true);
            const response = await fetchData(
                `/admin/category-requests/${selectedCategoryRequest.pk}/reject`,
                "POST",
                true,
                { justification: categoryRequestRejectReason }
            );
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("CategoryRequestRejected").d("Category Request Rejected"),
                    text: response.data?.detail || intl.get("CategoryRequestRejectedMessage").d("The category request has been rejected."),
                });
                setCategoryRequestRejectOpen(false);
                handleCloseCategoryRequestDetail();
                refreshCategoryRequestTable();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("FailedToRejectCategoryRequest").d("Failed to reject category request"),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToRejectCategoryRequest").d("Failed to reject category request"),
            });
        } finally {
            setActionLoading(false);
        }
    }, [categoryRequestRejectReason, selectedCategoryRequest, refreshCategoryRequestTable, handleCloseCategoryRequestDetail]);

    const getTableUrl = useCallback(() => {
        return "/admin/pending-approvals/table";
    }, []);

    const header = [
        {
            label: intl.get("Name").d("Name"),
            column_name: "name",
            filter: true,
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "cnpj",
            filter: true,
            function: (value) => formatters.formatCNPJ(value),
        },
        {
            label: intl.get("Categories").d("Categories"),
            column_name: "category",
            filter: true,
            control: "select",
            options: getInstitutionCategoryOptions(),
            sortable: false,
            element: (row) => {
                // Check if this is a category request - show with + icon and tooltip
                if (row.record_type === "category_request") {
                    const categoryName = getInstitutionCategoryLabel(row.category_requested || row.category);
                    return (
                        <Tooltip title={intl.get("CategoryRequest.NewCategory").d("New category request")}>
                            <Chip
                                label={categoryName}
                                size="small"
                                color="info"
                                icon={<AddCircleOutlineIcon fontSize="small" />}
                            />
                        </Tooltip>
                    );
                }

                // Use categories array if available, otherwise fall back to single category
                const categories = row.categories || (row.category ? [{ value: row.category }] : []);

                // Check if institution has BUYER category
                const categoryValues = categories.map(c => c.value);
                const hasBuyer = categoryValues.includes(InstitutionCategoryEnum.Buyer);

                // If has BUYER, show combined format in single chip
                if (hasBuyer) {
                    const formattedLabel = formatCategoriesForDisplay(categories);
                    return (
                        <Chip
                            label={formattedLabel}
                            size="small"
                            color="warning"
                            icon={<GroupsIcon fontSize="small" />}
                        />
                    );
                }

                // Otherwise, show individual chips
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {categories.map((cat) => {
                            const needsDirector = CATEGORIES_REQUIRING_DIRECTOR_APPROVAL.includes(cat.value);
                            const categoryName = getInstitutionCategoryLabel(cat.value);
                            return (
                                <Chip
                                    key={cat.value}
                                    label={categoryName}
                                    size="small"
                                    color={needsDirector ? "warning" : "default"}
                                    icon={needsDirector ? <GroupsIcon fontSize="small" /> : undefined}
                                />
                            );
                        })}
                        {categories.length === 0 && "-"}
                    </Box>
                );
            }
        },
        {
            label: intl.get("ApprovalStep").d("Approval Step"),
            column_name: "status_label",
            filter: false,
            sortable: false,
            element: (row) => (
                <Chip
                    label={row.status_label || intl.get("Unknown").d("Unknown")}
                    size="small"
                    color="info"
                    variant="outlined"
                />
            )
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "280px",
            sortable: false,
            filter: false,
            element: (row) => {
                const canApprove = canApproveInCurrentTab(row);
                const isDirectoriaStatus = row.status === InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA;
                const directorContext = row.user_director_context;

                // For DIRETORIA status, check if user's group already approved
                const userGroupAlreadyApproved = isDirectoriaStatus &&
                    directorContext?.is_director &&
                    !directorContext?.can_approve;

                // Get pending groups for display
                const pendingGroups = row.director_approval_status?.missing_groups || [];

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Tooltip title={intl.get("ViewDetails").d("View Details")}>
                            <IconButton size="small" onClick={() => handleViewDetails(row)}>
                                <VisibilityIcon />
                            </IconButton>
                        </Tooltip>

                        {userGroupAlreadyApproved ? (
                            <Tooltip title={
                                pendingGroups.length > 0
                                    ? `${intl.get("PendingGroups").d("Pending Groups")}: ${pendingGroups.map(g => g.name).join(", ")}`
                                    : ""
                            }>
                                <Chip
                                    icon={<HourglassEmptyIcon />}
                                    label={intl.get("YourGroupApproved").d("Your group approved")}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            </Tooltip>
                        ) : canApprove && (
                            <>
                                <Tooltip title={intl.get("Approve").d("Approve")}>
                                    <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleApproveInstitution(row)}
                                    >
                                        <CheckCircleIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={intl.get("Reject").d("Reject")}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleOpenRejectModal(row)}
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                );
            }
        },
    ];

    return (
        <DmsPage name={intl.get("InstitutionApprovals").d("Institution Approvals")} manual_slug="institution-approvals">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {intl.get("InstitutionApprovals").d("Institution Approvals")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTabber
                        pageName={intl.get("InstitutionApprovals").d("Institution Approvals")}
                        selected={currentTab}
                        onChange={(key) => setCurrentTab(key)}
                        tabs={[
                            {
                                name: "all",
                                title: intl.get("AllApprovals").d("All"),
                                component: (
                                    <DmsTable
                                        key="all"
                                        header={header}
                                        managed={true}
                                        needs_authentication={true}
                                        ref={tableAllRef}
                                        url={getTableUrl()}
                                        pre_filter={[]}
                                        export_table={false}
                                    />
                                )
                            },
                            {
                                name: "gestao_lena",
                                title: intl.get("GestaoLena").d("Gestão Lena"),
                                component: (
                                    <DmsTable
                                        key="gestao_lena"
                                        header={header}
                                        managed={true}
                                        needs_authentication={true}
                                        ref={tableGestaoLenaRef}
                                        url={getTableUrl()}
                                        pre_filter={[{ name: "approval_step", value: "gestao_lena" }]}
                                        export_table={false}
                                    />
                                )
                            },
                            {
                                name: "regulatorio",
                                title: intl.get("Regulatorio").d("Regulatório"),
                                component: (
                                    <DmsTable
                                        key="regulatorio"
                                        header={header}
                                        managed={true}
                                        needs_authentication={true}
                                        ref={tableRegulatorioRef}
                                        url={getTableUrl()}
                                        pre_filter={[{ name: "approval_step", value: "regulatorio" }]}
                                        export_table={false}
                                    />
                                )
                            },
                            {
                                name: "diretoria",
                                title: intl.get("Diretoria").d("Diretoria"),
                                component: (
                                    <DmsTable
                                        key="diretoria"
                                        header={header}
                                        managed={true}
                                        needs_authentication={true}
                                        ref={tableDiretoriaRef}
                                        url={getTableUrl()}
                                        pre_filter={[{ name: "approval_step", value: "diretoria" }]}
                                        export_table={false}
                                    />
                                )
                            }
                        ]}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            {/* Institution Details Modal */}
            <DmsModal
                isVisible={detailModalOpen}
                onClose={handleCloseDetailModal}
                maxWidth="md"
                fullWidth
            >
                <DmsModal.Header onClose={handleCloseDetailModal} title={intl.get("InstitutionDetails").d("Institution Details")} />
                <DmsModal.Body>
                    {selectedInstitution && (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {selectedInstitution.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                CNPJ: {formatters.formatCNPJ(selectedInstitution.cnpj)} | {
                                    selectedInstitution.categories && selectedInstitution.categories.length > 0
                                        ? formatCategoriesForDisplay(selectedInstitution.categories)
                                        : getInstitutionCategoryLabel(selectedInstitution.category)
                                }
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {intl.get("RegisteredAt").d("Registered at")}: {selectedInstitution.created_on ? new Date(selectedInstitution.created_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                                {" | "}
                                {intl.get("LastUpdated").d("Last Updated")}: {selectedInstitution.updated_on ? new Date(selectedInstitution.updated_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            {/* Institution Documents */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <DescriptionIcon color="action" />
                                <Typography variant="subtitle1">
                                    {intl.get("Documents").d("Documents")}
                                </Typography>
                            </Box>

                            {loadingDocuments ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : institutionDocuments.length === 0 ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {intl.get("NoDocumentsUploaded").d("No documents uploaded")}
                                </Alert>
                            ) : (
                                <List dense sx={{ mb: 2 }}>
                                    {institutionDocuments.map((doc, index) => (
                                        <ListItem
                                            key={doc.pk || index}
                                            sx={{
                                                bgcolor: "background.paper",
                                                borderRadius: 1,
                                                mb: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                            secondaryAction={
                                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                                    <Tooltip title={intl.get("Preview").d("Preview")}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handlePreviewDocument(doc)}
                                                            disabled={!doc.file?.pk && !doc.file_pk}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={intl.get("Download").d("Download")}>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            onClick={() => {
                                                                const filePk = doc.file?.pk || doc.file_pk;
                                                                downloadFile(filePk);
                                                            }}
                                                            disabled={!doc.file?.pk && !doc.file_pk}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            }
                                        >
                                            <ListItemIcon>
                                                <DescriptionIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={getTranslatedDocumentName(doc.document_name) || doc.file?.original_filename || intl.get("Document").d("Document")}
                                                secondary={
                                                    <Box>
                                                        {doc.file?.original_filename && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {intl.get("File").d("File")}: {doc.file.original_filename}
                                                            </Typography>
                                                        )}
                                                        {doc.is_exempt ? (
                                                            <>
                                                                <Chip
                                                                    label={intl.get("Document.ExpirationExempt").d("Expiry waived")}
                                                                    color="info"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ mt: 0.5 }}
                                                                />
                                                                {doc.exemption_note && (
                                                                    <Box sx={{ mt: 0.5, mb: 0.5, px: 1, py: 0.5, border: "1px solid", borderColor: "info.main", borderRadius: 1, maxWidth: "80%" }}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {doc.exemption_note}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </>
                                                        ) : doc.expiration_date ? (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {intl.get("ExpirationDate").d("Expiration Date")}: {formatDate(doc.expiration_date)}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            <Divider sx={{ my: 2 }} />

                            {/* Director Approval Status */}
                            {selectedInstitution.status === InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA && directorApprovalStatus && (
                                <>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                        <GroupsIcon color="action" />
                                        <Typography variant="subtitle1">
                                            {intl.get("DirectorApprovalStatus").d("Director Approval Status")}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                                        <Typography variant="body2">
                                            {intl.get("ApprovedGroups").d("Approved Groups")}: {directorApprovalStatus.approved_groups} / {directorApprovalStatus.total_groups}
                                        </Typography>
                                        {directorApprovalStatus.missing_groups?.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get("PendingGroups").d("Pending Groups")}:
                                                </Typography>
                                                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                                                    {directorApprovalStatus.missing_groups.map((group) => (
                                                        <Chip
                                                            key={group.pk}
                                                            label={group.name}
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* User's Director Context */}
                                    {directorContext && (
                                        <Box sx={{ mb: 2 }}>
                                            {directorContext.is_director ? (
                                                directorContext.can_approve ? (
                                                    <Alert severity="info" sx={{ mb: 1 }}>
                                                        <Typography variant="body2">
                                                            {intl.get("YourGroupLabel").d("Your group")}:{" "}
                                                            <strong>{directorContext.user_groups?.map(g => g.name).join(", ")}</strong>
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {intl.get("YourGroupCanApprove").d("You can approve or reject this institution.")}
                                                        </Typography>
                                                    </Alert>
                                                ) : (
                                                    <Alert severity="success" sx={{ mb: 1 }}>
                                                        <Typography variant="body2">
                                                            {intl.get("YourGroupLabel").d("Your group")}:{" "}
                                                            <strong>{directorContext.user_groups?.map(g => g.name).join(", ")}</strong>
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {intl.get("YourGroupAlreadyApproved").d("Your group has already approved this institution. Awaiting approval from other groups.")}
                                                        </Typography>
                                                    </Alert>
                                                )
                                            ) : (
                                                <Alert severity="warning" sx={{ mb: 1 }}>
                                                    <Typography variant="body2">
                                                        {intl.get("NotInDirectorGroup").d("You are not part of a director group for this approval.")}
                                                    </Typography>
                                                </Alert>
                                            )}
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />
                                </>
                            )}

                            {/* Approval History */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                <HistoryIcon color="action" />
                                <Typography variant="subtitle1">
                                    {intl.get("ApprovalHistory").d("Approval History")}
                                </Typography>
                            </Box>

                            {loadingHistory ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : approvalHistory.length === 0 ? (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {intl.get("NoApprovalHistory").d("No approval history yet")}
                                </Alert>
                            ) : (
                                <List dense>
                                    {approvalHistory.map((item, index) => (
                                        <ListItem
                                            key={item.pk || index}
                                            sx={{
                                                bgcolor: "background.paper",
                                                borderRadius: 1,
                                                mb: 1,
                                                border: "1px solid",
                                                borderColor: item.is_approval ? "success.light" : "error.light",
                                                borderLeft: "4px solid",
                                                borderLeftColor: item.is_approval ? "success.main" : "error.main",
                                            }}
                                        >
                                            <ListItemIcon>
                                                {item.is_approval ? (
                                                    <CheckCircleIcon color="success" />
                                                ) : (
                                                    <CancelIcon color="error" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {item.approver_role === "EUROFARMA_GESTAO_LENA"
                                                                ? intl.get("GestaoLena").d("Gestão Lena")
                                                                : item.approver_role === "EUROFARMA_REGULATORIO"
                                                                    ? intl.get("Regulatorio").d("Regulatório")
                                                                    : item.approver_role === "EUROFARMA_DIRETORIA"
                                                                        ? intl.get("Diretoria").d("Diretoria")
                                                                        : item.approver_role || intl.get("Unknown").d("Unknown")
                                                            }
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={item.is_approval
                                                                ? intl.get("Approved").d("Approved")
                                                                : intl.get("Rejected").d("Rejected")
                                                            }
                                                            color={item.is_approval ? "success" : "error"}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {intl.get("By").d("By")}: {item.action_by_name || "-"}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {intl.get("Date").d("Date")}: {item.action_on ? new Date(item.action_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                                                        </Typography>
                                                        {item.justification && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {intl.get("Justification").d("Justification")}: {item.justification}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {selectedInstitution?.status_label && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1, borderLeft: "4px solid", borderLeftColor: "primary.main" }}>
                                    <Typography variant="body2">
                                        <strong>{intl.get("CurrentStep").d("Current Step")}:</strong> {selectedInstitution.status_label}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DmsModal.Body>
                {selectedInstitution && canApproveInCurrentTab(selectedInstitution) && (
                    selectedInstitution.status !== InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA ||
                    (directorContext && directorContext.can_approve)
                ) && (
                    <DmsModal.Footer>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                            <DmsButton
                                variant="danger"
                                text={intl.get("Reject").d("Reject")}
                                onClick={() => {
                                    handleCloseDetailModal();
                                    handleOpenRejectModal(selectedInstitution);
                                }}
                                disabled={actionLoading}
                            />
                            <DmsButton
                                variant="primary"
                                text={intl.get("Approve").d("Approve")}
                                onClick={() => handleApproveInstitution(selectedInstitution)}
                                disabled={actionLoading}
                            />
                        </Box>
                    </DmsModal.Footer>
                )}
            </DmsModal>

            {/* Rejection Modal */}
            <DmsModal
                isVisible={rejectModalOpen}
                onClose={() => setRejectModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DmsModal.Header onClose={() => setRejectModalOpen(false)} title={intl.get("RejectInstitution").d("Reject Institution")} />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            {intl.get("RejectInstitutionMessage").d("Please provide a reason for rejecting this institution:")}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={intl.get("EnterRejectionReason").d("Enter rejection reason...")}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={() => setRejectModalOpen(false)}
                        />
                        <DmsButton
                            variant="danger"
                            text={intl.get("Reject").d("Reject")}
                            onClick={handleRejectInstitution}
                            disabled={actionLoading || !rejectReason.trim()}
                        />
                    </Box>
                </DmsModal.Footer>
            </DmsModal>

            {/* Approval Confirmation Modal */}
            <DmsModal
                isVisible={approveModalOpen}
                onClose={() => {
                    setApproveModalOpen(false);
                    setInstitutionToApprove(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DmsModal.Header onClose={() => {
                    setApproveModalOpen(false);
                    setInstitutionToApprove(null);
                }} title={intl.get("ConfirmApproval").d("Confirm Approval")} />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            {intl.get("ConfirmInstitutionApprovalMessage").d("Are you sure you want to approve this institution?")}
                        </Typography>
                        {institutionToApprove && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>{intl.get("Name").d("Name")}:</strong> {institutionToApprove.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>CNPJ:</strong> {formatters.formatCNPJ(institutionToApprove.cnpj)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>{intl.get("Category").d("Category")}:</strong> {
                                            institutionToApprove.categories && institutionToApprove.categories.length > 0
                                                ? formatCategoriesForDisplay(institutionToApprove.categories)
                                                : getInstitutionCategoryLabel(institutionToApprove.category)
                                        }
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>{intl.get("RegisteredAt").d("Registered at")}:</strong> {institutionToApprove.created_on ? new Date(institutionToApprove.created_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>{intl.get("LastUpdated").d("Last Updated")}:</strong> {institutionToApprove.updated_on ? new Date(institutionToApprove.updated_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                                    </Typography>
                                </Box>
                                {institutionToApprove.status_label && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1, borderLeft: "4px solid", borderLeftColor: "primary.main" }}>
                                        <Typography variant="body2">
                                            <strong>{intl.get("CurrentStep").d("Current Step")}:</strong> {institutionToApprove.status_label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {intl.get("ApprovalStepMessage").d("Approving this step will advance to the next phase of the approval flow.")}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={approveJustification}
                            onChange={(e) => setApproveJustification(e.target.value)}
                            placeholder={intl.get("OptionalJustification").d("Optional justification...")}
                            label={intl.get("Justification").d("Justification")}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={() => {
                                setApproveModalOpen(false);
                                setInstitutionToApprove(null);
                            }}
                        />
                        <DmsButton
                            variant="primary"
                            text={intl.get("Approve").d("Approve")}
                            onClick={executeApproveInstitution}
                            disabled={actionLoading}
                        />
                    </Box>
                </DmsModal.Footer>
            </DmsModal>

            {/* Category Request Details Modal */}
            <DmsModal
                isVisible={categoryRequestDetailOpen}
                onClose={handleCloseCategoryRequestDetail}
                maxWidth="sm"
                fullWidth
            >
                <DmsModal.Header onClose={handleCloseCategoryRequestDetail} title={intl.get("CategoryRequestDetails").d("Category Request Details")} />
                <DmsModal.Body>
                    {selectedCategoryRequest && (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {selectedCategoryRequest.institution_name || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                CNPJ: {selectedCategoryRequest.cnpj || "-"}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {intl.get("RequestedCategory").d("Requested Category")}
                                </Typography>
                                <Chip
                                    icon={<CategoryIcon />}
                                    label={selectedCategoryRequest.category_name || getInstitutionCategoryLabel(selectedCategoryRequest.category)}
                                    color="secondary"
                                    sx={{ mt: 1 }}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {intl.get("CurrentStep").d("Current Step")}
                                </Typography>
                                <Chip
                                    label={selectedCategoryRequest.status_label || intl.get("Unknown").d("Unknown")}
                                    color="info"
                                    variant="outlined"
                                    sx={{ mt: 1 }}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {intl.get("RequestedOn").d("Requested On")}
                                </Typography>
                                <Typography variant="body1">
                                    {selectedCategoryRequest.requested_on
                                        ? new Date(selectedCategoryRequest.requested_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                        : "-"}
                                </Typography>
                            </Box>

                            {selectedCategoryRequest.status_justification && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>{intl.get("Justification").d("Justification")}:</strong> {selectedCategoryRequest.status_justification}
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    )}
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Close").d("Close")}
                            onClick={handleCloseCategoryRequestDetail}
                        />
                        {selectedCategoryRequest && (
                            (selectedCategoryRequest.required_role === "EUROFARMA_GESTAO_LENA" && approvalPermissions.can_approve_gestao_lena) ||
                            (selectedCategoryRequest.required_role === "EUROFARMA_REGULATORIO" && approvalPermissions.can_approve_regulatorio) ||
                            (selectedCategoryRequest.required_role === "EUROFARMA_DIRETORIA" && approvalPermissions.can_approve_diretoria)
                        ) && (
                            <>
                                <DmsButton
                                    variant="danger"
                                    text={intl.get("Reject").d("Reject")}
                                    onClick={() => {
                                        handleCloseCategoryRequestDetail();
                                        handleOpenCategoryRequestRejectModal(selectedCategoryRequest);
                                    }}
                                    disabled={actionLoading}
                                />
                                <DmsButton
                                    variant="primary"
                                    text={intl.get("Approve").d("Approve")}
                                    onClick={() => handleApproveCategoryRequest(selectedCategoryRequest)}
                                    disabled={actionLoading}
                                />
                            </>
                        )}
                    </Box>
                </DmsModal.Footer>
            </DmsModal>

            {/* Category Request Rejection Modal */}
            <DmsModal
                isVisible={categoryRequestRejectOpen}
                onClose={() => setCategoryRequestRejectOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DmsModal.Header onClose={() => setCategoryRequestRejectOpen(false)} title={intl.get("RejectCategoryRequest").d("Reject Category Request")} />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            {intl.get("RejectCategoryRequestMessage").d("Please provide a reason for rejecting this category request:")}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={categoryRequestRejectReason}
                            onChange={(e) => setCategoryRequestRejectReason(e.target.value)}
                            placeholder={intl.get("EnterRejectionReason").d("Enter rejection reason...")}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={() => setCategoryRequestRejectOpen(false)}
                        />
                        <DmsButton
                            variant="danger"
                            text={intl.get("Reject").d("Reject")}
                            onClick={handleRejectCategoryRequest}
                            disabled={actionLoading || !categoryRequestRejectReason.trim()}
                        />
                    </Box>
                </DmsModal.Footer>
            </DmsModal>

            {/* Category Request Approval Confirmation Modal */}
            <DmsModal
                isVisible={categoryRequestApproveOpen}
                onClose={() => {
                    setCategoryRequestApproveOpen(false);
                    setSelectedCategoryRequest(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DmsModal.Header onClose={() => {
                    setCategoryRequestApproveOpen(false);
                    setSelectedCategoryRequest(null);
                }} title={intl.get("ConfirmCategoryRequestApproval").d("Confirm Category Request Approval")} />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            {intl.get("ConfirmCategoryRequestApprovalMessage").d("Are you sure you want to approve this category request?")}
                        </Typography>
                        {selectedCategoryRequest && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>{intl.get("Institution").d("Institution")}:</strong> {selectedCategoryRequest.institution_name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>CNPJ:</strong> {selectedCategoryRequest.cnpj}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>{intl.get("Category").d("Category")}:</strong> {selectedCategoryRequest.category_name || getInstitutionCategoryLabel(selectedCategoryRequest.category)}
                                    </Typography>
                                </Box>
                                {selectedCategoryRequest.status_label && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1, borderLeft: "4px solid", borderLeftColor: "primary.main" }}>
                                        <Typography variant="body2">
                                            <strong>{intl.get("CurrentStep").d("Current Step")}:</strong> {selectedCategoryRequest.status_label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {intl.get("CategoryRequestApprovalStepMessage").d("Approving this step will advance to the next phase of the approval flow.")}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={categoryRequestApproveJustification}
                            onChange={(e) => setCategoryRequestApproveJustification(e.target.value)}
                            placeholder={intl.get("OptionalJustification").d("Optional justification...")}
                            label={intl.get("Justification").d("Justification")}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={() => {
                                setCategoryRequestApproveOpen(false);
                                setSelectedCategoryRequest(null);
                            }}
                        />
                        <DmsButton
                            variant="primary"
                            text={intl.get("Approve").d("Approve")}
                            onClick={executeApproveCategoryRequest}
                            disabled={actionLoading}
                        />
                    </Box>
                </DmsModal.Footer>
            </DmsModal>

            {/* Document Preview Modal */}
            <DmsFileViewerModal
                isOpen={previewModalOpen}
                onClose={handleClosePreview}
                filePk={previewDocument?.file?.pk || previewDocument?.file_pk}
                fileName={previewDocument?.file?.original_filename || previewDocument?.document_name}
                title={previewDocument?.document_name || previewDocument?.file?.original_filename}
            />
        </DmsPage>
    );
};
